import { useEffect, useRef, useState } from 'react'
import { ILiveSources, liveSourcesDefault } from '../../../utils'
import { isIOS, isIosApp, isMobile, isNativeApp } from '../../../client'
import { IUseLiveSourceManifest } from './useLiveSourceManifest'
import { useGeoManifestFetchStatus } from './useGeoManifestFetch'

// The PAL <Script> in Scripts.tsx only renders when SiteMeta.config.player.sendPal
// is exactly 'on' - on pages where it's unset/misconfigured, googlePalReady never
// moves off its initial `false` (onReady/onError never fire, there's no script to
// fire them). Without this timeout the live source would wait forever.
// 1.5s is the agreed middle between what the two players used before they
// shared this hook: OTT waited 1s, the main site 2s.
const PAL_TIMEOUT_MS = 1500

// useGooglePal reports "no nonce" as one of these sentinel strings rather than
// an empty value, so they must not reach the manifest URL verbatim.
const PAL_SENTINELS = ['undefined', 'off']

// RESH13-1148: gmana tags every stream with where it is being played.
// The values are the ticket's verbatim, including the misspelled 'andriod' -
// the receiving side matches the literal string, so do not "fix" it here.
const getStreamPlatform = () => {
  // isNativeApp() rather than getAdDeviceType() === 'app': that one also
  // requires a narrow viewport, so a webview on a tablet would report as
  // desktop, and the ticket wants every webview under ma*.
  const inApp = isNativeApp()
  if (!inApp && !isMobile()) {
    return 'desktop'
  }
  // The native shell reports its own platform; fall back to the user agent
  // when a webview does not expose it. Anything not identifiable as iOS is
  // reported as Android, since the ticket defines no other mobile value.
  const ios = inApp ? isIosApp() || isIOS() : isIOS()
  return `${inApp ? 'ma' : 'mw'}${ios ? 'ios' : 'andriod'}`
}

interface IUseLiveSourceProps {
  // Live and eligible for a gmana source: the caller decides what that means
  // (main site: isLive && !inChanel26; OTT: fetched asset on channel 13).
  enabled: boolean
  liveSources: ILiveSources | undefined
  subTitlesOn: boolean
  googlePal: string | null
  googlePalReady: boolean | 'error'
  // Same ppid handed to plugins.ima and google.ima.settings.setPpid, so the
  // SSAI ad requests gmana makes server-side correlate with the client-side
  // IMA ones in GAM. Callers source it from getPpid(), which persists it.
  ppid?: string | null
}

export function useLiveSource({
  enabled,
  liveSources,
  subTitlesOn,
  googlePal,
  googlePalReady,
  ppid,
}: IUseLiveSourceProps): IUseLiveSourceManifest {
  const [liveSource, setLiveSource] = useState<IUseLiveSourceManifest>(null)
  const [palTimedOut, setPalTimedOut] = useState(false)
  // The source is built once, so it must wait for geo to settle - otherwise
  // it locks in the default 'Israel' and never switches to the global stream.
  const geo = useGeoManifestFetchStatus()

  // Read at the moment PAL resolves rather than depended on, so a subtitle
  // toggle or a config refresh can't rebuild the source (and with it the whole
  // Kaltura player) mid-playback - matching what both callers did inline.
  const inputs = useRef({ liveSources, subTitlesOn, ppid })
  useEffect(() => {
    inputs.current = { liveSources, subTitlesOn, ppid }
  }, [liveSources, subTitlesOn, ppid])

  useEffect(() => {
    if (!enabled || googlePal) {
      return
    }
    const timer = setTimeout(() => setPalTimedOut(true), PAL_TIMEOUT_MS)
    return () => clearTimeout(timer)
  }, [enabled, googlePal])

  useEffect(() => {
    // Google PAL is fetched from the same host our own AdBlock probe targets
    // (imasdk.googleapis.com), so an ad blocker kills it too. Without this
    // fallback the live source never gets built - `googlePal` stays '' forever -
    // leaving LIVE with a blank player, even though LIVE is meant to never be
    // blocked (its ads are SSAI).
    const palBlocked = googlePalReady === 'error' || palTimedOut
    if (!enabled || !geo.settled || !(googlePal || palBlocked)) {
      return
    }
    const { liveSources, subTitlesOn, ppid } = inputs.current
    // setClientConfig assigns clientConfig.liveSources unconditionally, so a
    // page whose props omit it (the embed route does) clobbers the default to
    // undefined. Fall back rather than build an "undefined?player.pal=..." URL.
    const sources = liveSources || liveSourcesDefault
    // Abroad gets the single global stream (no platform/subtitles split);
    // without a `global` entry in live.json it keeps the Israel streams.
    const platform =
      geo.country !== 'Israel' && sources.global?.live
        ? sources.global
        : isMobile()
        ? sources.mobile
        : sources.desktop
    const source =
      'live' in platform
        ? platform.live
        : platform[subTitlesOn ? 'live_with_subs' : 'live_no_subs']

    const params = [
      `player.pal=${
        !googlePal || PAL_SENTINELS.includes(googlePal) ? '' : googlePal
      }`,
    ]
    const dmpid = localStorage.getItem('dmp-publisher-dmpid')
    if (dmpid) {
      params.push(`player.idxu=${dmpid}`)
    }
    const dxsegvalue = localStorage.getItem('dmp-publisher-prevDefinitionsIds')
    if (dxsegvalue) {
      params.push(`player.idxseg=${dxsegvalue}`)
    }
    if (ppid) {
      params.push(`player.ppid=${ppid}`)
    }
    // The ticket's table writes this one bare, but every param on this URL is
    // namespaced, so it goes out as player.platform alongside player.pal,
    // player.idxu and player.ppid.
    params.push(`player.platform=${getStreamPlatform()}`)

    const nextSource = `${source}?${params.join('&')}`
    const nextProvider = platform.provider || 'gmana'
    // Identity matters: both callers feed this object straight into their
    // KalturaPlayer.setup() effect, so handing back an equal-but-new object
    // would tear down and rebuild the player for nothing.
    setLiveSource(prev =>
      prev && prev.source === nextSource && prev.provider === nextProvider
        ? prev
        : { source: nextSource, provider: nextProvider }
    )
  }, [
    enabled,
    googlePal,
    googlePalReady,
    palTimedOut,
    geo.settled,
    geo.country,
  ])

  return liveSource
}
